import cx from 'classnames';
import React, { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';

import * as styles from './styles.css';
import { ForwardRefComponent } from '../../../Types';
import Box, { BoxProps } from '../Box';
import Spinner from '../Spinner';

export interface ButtonProps extends BoxProps {
  level?: 'primary' | 'secondary' | 'destructive';
  processing?: boolean;
}

const Button: ForwardRefComponent<ButtonProps> = forwardRef(
  (
    {
      element = 'button',
      level = 'secondary',
      className,
      children,
      processing,
      disabled,
      onMouseLeave,
      onMouseUp,
      ...rest
    },
    ref,
  ) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    useImperativeHandle(ref, () => buttonRef.current!);

    const classNames = cx(
      styles['button'],
      styles[`level-${level}`],
      { [styles['is-processing']]: processing },
      className,
    );

    const handleMouseLeave = useCallback(
      (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        buttonRef.current?.blur();
        onMouseLeave?.(event);
      },
      [onMouseLeave],
    );

    const handleMouseUp = useCallback(
      (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        buttonRef.current?.blur();
        onMouseUp?.(event);
      },
      [onMouseUp],
    );

    return (
      <Box
        ref={buttonRef}
        element={element}
        className={classNames}
        disabled={disabled || processing}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        {...rest}
      >
        {children}
        {processing && <Spinner className={styles['spinner']} inverse={level === 'primary'} />}
      </Box>
    );
  },
);

export default Button;
